/**
 * API de HCE: historia clínica, episodios, diagnósticos, búsqueda CIE-10.
 */
import { apiFetch } from "./client";

export interface Cie10Item {
  codigo: string;
  descripcion: string;
  capitulo: string | null;
}

export type DiagnosticoTipo = "PRINCIPAL" | "SECUNDARIO";
export type DiagnosticoCondicion = "PRESUNTIVO" | "DEFINITIVO";
export type EpisodioEstado = "ABIERTO" | "CERRADO" | "ANULADO";

export interface DiagnosticoIn {
  cie10_codigo: string;
  tipo: DiagnosticoTipo;
  condicion: DiagnosticoCondicion;
}

export interface DiagnosticoOut {
  id: number;
  cie10_codigo: string;
  cie10_descripcion: string;
  tipo: DiagnosticoTipo;
  condicion: DiagnosticoCondicion;
}

export interface EpisodioCreate {
  paciente_id: number;
  cita_id?: number | null;
  especialidad_id?: number | null;
  motivo_consulta?: string | null;
  anamnesis?: string | null;
  examen_fisico?: string | null;
  plan?: string | null;
  diagnosticos: DiagnosticoIn[];
  cerrar: boolean;
}

export interface EpisodioUpdate {
  motivo_consulta?: string | null;
  anamnesis?: string | null;
  examen_fisico?: string | null;
  plan?: string | null;
  diagnosticos?: DiagnosticoIn[];
  cerrar?: boolean;
}

export interface Enmienda {
  id: number;
  episodio_id: number;
  motivo: string;
  texto: string;
  profesional_nombre: string;
  profesional_colegiatura: string;
  created_at: string;
}

export interface EnmiendaCreate {
  motivo: string;
  texto: string;
}

export interface Episodio {
  id: number;
  historia_id: number;
  paciente_id: number;
  paciente_nombre: string;
  profesional_id: number;
  profesional_nombre: string;
  profesional_colegiatura: string;
  especialidad_id: number | null;
  especialidad_nombre: string | null;
  inicio: string;
  fin: string | null;
  motivo_consulta: string | null;
  anamnesis: string | null;
  examen_fisico: string | null;
  plan: string | null;
  estado: EpisodioEstado;
  firmado_en: string | null;
  diagnosticos: DiagnosticoOut[];
  /** Correcciones posteriores a la firma; el original nunca se modifica. */
  enmiendas: Enmienda[];
  created_at: string;
}

export interface EpisodioListItem {
  id: number;
  inicio: string;
  estado: EpisodioEstado;
  profesional_id: number;
  profesional_nombre: string;
  especialidad_nombre: string | null;
  dx_principal: string | null;
  dx_principal_codigo: string | null;
}

export interface Historia {
  id: number;
  numero: string;
  apertura: string;
  paciente_id: number;
  episodios: EpisodioListItem[];
}

export function searchCie10(q: string, limit = 30): Promise<Cie10Item[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return apiFetch<Cie10Item[]>(`/v1/cie10?${params.toString()}`);
}

export function getHistoriaDePaciente(pacienteId: number): Promise<Historia | null> {
  return apiFetch<Historia | null>(`/v1/pacientes/${pacienteId}/historia`);
}

export function createEpisodio(payload: EpisodioCreate): Promise<Episodio> {
  return apiFetch<Episodio>("/v1/episodios", { method: "POST", body: payload });
}

export function getEpisodio(id: number): Promise<Episodio> {
  return apiFetch<Episodio>(`/v1/episodios/${id}`);
}

export function updateEpisodio(id: number, payload: EpisodioUpdate): Promise<Episodio> {
  return apiFetch<Episodio>(`/v1/episodios/${id}`, { method: "PATCH", body: payload });
}

/**
 * Añade una corrección a un episodio ya firmado.
 *
 * No edita el original: la historia clínica conserva qué se registró y qué se
 * corrigió después, con autor y fecha. Devuelve el episodio completo.
 */
export function crearEnmienda(episodioId: number, payload: EnmiendaCreate): Promise<Episodio> {
  return apiFetch<Episodio>(`/v1/episodios/${episodioId}/enmiendas`, {
    method: "POST",
    body: payload,
  });
}
